import { useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSession } from '../hooks/useSession';
import { ScrollToTop } from '../components/ScrollToTop';
import { TooltipProvider } from '@/components/ui/tooltip';
import { TracksAdmin } from './TracksAdmin';
import { LessonsAdmin } from './LessonsAdmin';
import { ChecklistsAdmin, GradeFieldsAdmin, PathAdmin, PhotoRulesAdmin } from './ToolsAdmin';
import { SampleShotsAdmin } from './SampleShotsAdmin';
import { OverviewAdmin } from './OverviewAdmin';

/**
 * The console shell.
 *
 * Reuses the portal's sidebar chrome so staff aren't learning a second visual language,
 * but it is a separate tree: an admin has no vendor session, so none of the portal's
 * pages (which read `useContent`) can render for them.
 */
const NAV = [
  { to: '/admin/overview', label: 'Overview' },
  { to: '/admin/tracks', label: 'Tracks & lessons' },
  { to: '/admin/path', label: 'The guided path' },
  { to: '/admin/checklists', label: 'Checklists' },
  { to: '/admin/photo-rules', label: 'Photo standard' },
  { to: '/admin/sample-photos', label: 'Sample photos' },
  { to: '/admin/grade-fields', label: 'Listing checker' },
];

export function AdminApp() {
  const { admin, signOut } = useSession();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    // One provider for the whole console; every row action's tooltip lives under it.
    <TooltipProvider delayDuration={200}>
    <div className="vu-app">
      <ScrollToTop />
      <aside className="vu-side" data-open={menuOpen ? 'true' : 'false'} onClick={() => setMenuOpen(false)}>
        <div className="vu-brand">
          <div className="vu-brand-row">
            <img
              src="/shipindia-mark.png"
              width={26}
              height={26}
              alt=""
              aria-hidden="true"
              style={{ display: 'block', objectFit: 'contain' }}
            />
            <span className="vu-brand-name">ShipIndia</span>
          </div>
          <div className="vu-brand-sub">Vendor University</div>
          <div className="vu-brand-tag">Content console</div>
        </div>

        <div className="vu-navgroup">
          <div className="vu-navlabel">Content</div>
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="vu-navitem"
              aria-current={pathname.startsWith(item.to) ? 'page' : undefined}
            >
              <span className="vu-navitem-label">{item.label}</span>
            </Link>
          ))}
        </div>

        <div className="vu-support">
          <div style={{ fontWeight: 600, color: '#fff' }}>{admin?.name}</div>
          <div style={{ marginBottom: 'var(--space-3)' }}>{admin?.email} · {admin?.role}</div>
          <button type="button" className="vu-btn vu-btn-onink" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </aside>

      {menuOpen && (
        <button type="button" className="vu-scrim" aria-label="Close menu" onClick={() => setMenuOpen(false)} />
      )}

      <main className="vu-main">
        {/* The console has no search bar, so it gets a minimal bar that exists purely to
            carry the drawer toggle below 900px. */}
        <header className="vu-top">
          <button type="button" className="vu-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
          <div style={{ flex: 1, fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)' }}>
            Content console
          </div>
        </header>

        <Routes>
          <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
          <Route path="/admin/overview" element={<OverviewAdmin />} />
          <Route path="/admin/tracks" element={<TracksAdmin />} />
          <Route path="/admin/tracks/:trackId" element={<LessonsAdmin />} />
          <Route path="/admin/path" element={<PathAdmin />} />
          <Route path="/admin/checklists" element={<ChecklistsAdmin />} />
          <Route path="/admin/photo-rules" element={<PhotoRulesAdmin />} />
          <Route path="/admin/sample-photos" element={<SampleShotsAdmin />} />
          <Route path="/admin/grade-fields" element={<GradeFieldsAdmin />} />
          {/* An admin has no vendor session, so send stray portal URLs somewhere real. */}
          <Route path="*" element={<Navigate to="/admin/overview" replace />} />
        </Routes>
      </main>
    </div>
    </TooltipProvider>
  );
}
